'use client';

import { useTranslations } from 'next-intl';
import * as Tabs from '@radix-ui/react-tabs';
import classnames from 'clsx';
import CouponCard from './coupon-card';
import Empty from '@/components/ui-feat/empty';
import emptyImg from '../../images/empty.png';
import IconFont from '@/components/ui/iconFont';
import type { UIItem } from '../adapter';
import { showUsedDrawer } from '../../used-drawer';
import { Spin } from '@/components/ui-feat/spin';
import { track } from '@/utils/track';
import InfiniteScroll from '@/components/ui-feat/infinite-scroll';
import { useMemo } from 'react';


export interface TabOption {
  value: string;
  label: string;
  label_en?: string;
}

interface SecondaryTabsProps {
  value: string;
  onValueChange: (value: string) => void;
  tabs: TabOption[];
  items: UIItem[];
  isFirstLoad: boolean;
  isEmpty?: boolean;
  hasMore: boolean;
  onLoadMore: () => Promise<void>;
  onUse: (item: UIItem) => void;
}

export default function SecondaryTabs({
  value,
  onValueChange,
  tabs,
  items,
  isFirstLoad,
  isEmpty,
  hasMore,
  onLoadMore,
  onUse,
}: SecondaryTabsProps) {
  const t = useTranslations('reward');
  const commonT = useTranslations('common');

  const labelEn = useMemo(() => {
    return tabs.find(t => t.value === value)?.label_en;
  }, [value, tabs]);

  return (
    <Tabs.Root
      value={value}
      onValueChange={onValueChange}
      className="flex-1 flex flex-col overflow-hidden"
    >
      <Tabs.List className="flex-shrink-0 flex justify-around border-b border-[#ffffff10]">
        {tabs.map((tab) => (
          <Tabs.Trigger
            key={tab.value}
            value={tab.value}
            className={classnames(
              "relative text-[14px] font-semibold pb-0.5 mb-2.5 transition-colors duration-200 outline-none after:content-[''] after:absolute after:bottom-0 after:left-1/2 after:h-[2px] after:w-[18px] after:-translate-x-1/2 after:rounded-full after:bg-[#00bbff] after:opacity-0 after:scale-x-50 after:transition-all after:duration-250 after:ease-out",
              'data-[state=active]:text-[#00bbff] data-[state=inactive]:text-[#6b7a99]',
              'data-[state=active]:after:opacity-100 data-[state=active]:after:scale-x-100',
            )}
          >
            {tab.label}
          </Tabs.Trigger>
        ))}
      </Tabs.List>

      <div className="flex-1 overflow-y-auto no-scrollbar pb-6 pt-5">
        {isFirstLoad ? (
          <Spin className="h-full" />
        ) : isEmpty ? (
          <Empty
            className="h-full"
            image={emptyImg}
            imageSize={{ width: 62, height: 62 }}
            tip={
              <div className="text-center text-secondary text-xs pt-3">
                <div>{t('noItems')}</div>
                <div className="text-tertiary text-xss pt-2">{t('earnHint')}</div>
              </div>
            }
          />
        ) : (
          <div className="space-y-2.5">
            {items.map((item) => (
              <CouponCard
                key={item.item_id}
                item={item}
                tabs={tabs}
                onUse={() => onUse(item)}
              />
            ))}
            {/* 无限滚动组件 */}
            <InfiniteScroll
              hasMore={hasMore}
              loadMore={onLoadMore}
              noMoreText={items?.length > 4 ? commonT('noMoreData') : ''}
            />
          </div>
        )}
      </div>

      <div
        onClick={() => {
          track('prop_button_click', {
            current_page: labelEn,
            button_name: 'view invalid prop',
          });
          showUsedDrawer({
            tabs: tabs || [],
          });
        }}
      >
        <div className="flex-shrink-0 flex items-center justify-center py-5 cursor-pointer">
          <span className="text-[11px] text-[#00BBFF] font-medium">{t('viewInvalid')}</span>
          <IconFont name="arrow-right" size={10} color="#00BBFF" className="ml-1" />
        </div>
      </div>
    </Tabs.Root>
  );
}
